<template>
  <!-- The box holds whichever step is showing and animates its height on each
       swap, so the stats below glide instead of jumping. -->
  <div ref="box" class="lp-contest">
    <!-- Step 1: email and consent -->
    <form
      v-if="step === 'email'"
      class="lp-form"
      novalidate
      @submit.prevent="onSubmit()"
    >
      <div class="lp-form__row">
        <input
          v-model="email"
          class="lp-form__input"
          type="email"
          inputmode="email"
          autocomplete="email"
          :aria-label="props.emailLabel"
          :placeholder="props.emailPlaceholder"
          :disabled="isSubmitting"
          :aria-invalid="showEmailError"
        />
        <button class="btn lp-form__submit" type="submit" :disabled="isSubmitting">
          {{ props.submitLabel }}
        </button>
      </div>

      <label class="lp-form__consent" :class="{ 'is-invalid': showConsentError }">
        <input
          v-model="consent"
          class="lp-form__consent-input"
          type="checkbox"
          :disabled="isSubmitting"
          :aria-describedby="showConsentError ? consentErrorId : undefined"
        />
        <span class="lp-form__switch" aria-hidden="true"></span>
        <!-- Consent copy carries the privacy policy and contest rules links,
             interpolated with real routes by the Blade template. -->
        <span v-html="props.consentHtml"></span>
      </label>

      <p v-if="showEmailError" class="lp-form__error">
        {{ $t("formError.email") }}
      </p>
      <p
        v-else-if="showConsentError"
        :id="consentErrorId"
        class="lp-form__error"
        v-html="props.consentErrorHtml"
      ></p>
      <p v-else-if="failed" class="lp-form__error" role="alert">
        {{ props.errorLabel }}
      </p>

      <input
        v-model="honeypot"
        type="text"
        class="lp-form__trap"
        tabindex="-1"
        autocomplete="off"
        aria-hidden="true"
      />
    </form>

    <!-- Step 2: optional answer for the main prize -->
    <div v-else-if="step === 'answer'" class="lp-answer">
      <p ref="lead" class="lp-answer__lead" tabindex="-1">
        <span>{{ props.successLabel }}</span>
        <span>{{ props.answerCopy.question }}</span>
      </p>

      <form class="lp-form" novalidate @submit.prevent="onAnswer()">
        <div class="lp-form__row">
          <div class="lp-form__field">
            <input
              v-model="answer"
              class="lp-form__input"
              :class="{ 'has-count': answer.length > 0 }"
              type="text"
              :maxlength="ANSWER_MAX"
              autocomplete="off"
              :aria-label="props.answerCopy.label"
              :aria-describedby="helpId"
              :placeholder="props.answerCopy.placeholder"
              :disabled="isSubmitting"
            />
            <span v-if="answer.length > 0" class="lp-form__count" aria-hidden="true">
              {{ answer.length }}/{{ ANSWER_MAX }}
            </span>
          </div>
          <button class="btn lp-form__submit" type="submit" :disabled="isSubmitting">
            {{ props.answerCopy.send }}
          </button>
        </div>

        <p :id="helpId" class="lp-form__help">{{ props.answerCopy.helper }}</p>
        <p v-if="failed" class="lp-form__error" role="alert">
          {{ props.errorLabel }}
        </p>
        <button type="button" class="lp-form__skip" @click="finish('skipped')">
          {{ props.answerCopy.skip }}
        </button>
      </form>
    </div>

    <!-- Final states -->
    <div v-else ref="done" class="lp-done" tabindex="-1">
      <p class="lp-done__title">
        {{ step === "answered" ? props.answerCopy.received : props.answerCopy.skipped }}
      </p>
      <p class="lp-done__text">
        {{
          step === "answered"
            ? props.answerCopy.received_note
            : props.answerCopy.skipped_note
        }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { sendPixelEvent } from "../cookie-consent/meta-pixel";
import { submitContestEntry } from "./contest.api";

interface AnswerCopy {
  question: string;
  label: string;
  placeholder: string;
  helper: string;
  send: string;
  skip: string;
  received: string;
  received_note: string;
  skipped: string;
  skipped_note: string;
}

const props = defineProps<{
  emailLabel: string;
  emailPlaceholder: string;
  submitLabel: string;
  successLabel: string;
  errorLabel: string;
  consentHtml: string;
  consentErrorHtml: string;
  answerCopy: AnswerCopy;
}>();

const ANSWER_MAX = 200;

type Step = "email" | "answer" | "answered" | "skipped";

const step = ref<Step>("email");
const email = ref("");
const answer = ref("");
const honeypot = ref("");
const consent = ref(false);
const isSubmitting = ref(false);
const failed = ref(false);
// Nothing is marked invalid until the first submit attempt; after that the
// messages clear themselves as soon as the field becomes valid.
const submitted = ref(false);

const box = ref<HTMLElement | null>(null);
const lead = ref<HTMLElement | null>(null);
const done = ref<HTMLElement | null>(null);

const consentErrorId = "lp-consent-error";
const helpId = "lp-answer-help";

const isEmailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.value.trim()));

const showEmailError = computed(
  () => submitted.value && !isEmailValid.value,
);
const showConsentError = computed(
  () => submitted.value && isEmailValid.value && !consent.value,
);

/*
 * Change the step and animate the box from its old height to the new one. The
 * inline height is released afterwards so the box always fits its content.
 */
const goTo = async (next: Step): Promise<void> => {
  const el = box.value;
  const from = el?.offsetHeight ?? 0;
  step.value = next;
  failed.value = false;
  await nextTick();

  if (el && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    const to = el.offsetHeight;

    if (from !== to) {
      el.style.height = `${from}px`;
      el.getBoundingClientRect(); // commit the start height before transitioning
      el.classList.add("is-resizing");
      el.style.height = `${to}px`;

      const release = (): void => {
        el.removeEventListener("transitionend", onEnd);
        el.classList.remove("is-resizing");
        el.style.height = "";
      };
      // transitionend bubbles up from the buttons inside: only the box's own.
      const onEnd = (e: TransitionEvent): void => {
        if (e.target === el && e.propertyName === "height") {
          release();
        }
      };
      el.addEventListener("transitionend", onEnd);
      window.setTimeout(release, 400); // in case transitionend never fires
    }
  }

  // Hand focus to the new content so keyboard and screen reader users follow.
  (lead.value ?? done.value)?.focus({ preventScroll: true });
};

const finish = (to: "answered" | "skipped"): Promise<void> => goTo(to);

const onSubmit = async (): Promise<void> => {
  submitted.value = true;
  failed.value = false;

  if (!isEmailValid.value || !consent.value) {
    return;
  }

  isSubmitting.value = true;
  try {
    await submitContestEntry({
      email: email.value.trim(),
      honeypot: honeypot.value,
    });
    // Counted once, here: step 2 is the same person. Bots never get a Lead.
    if (!honeypot.value) {
      sendPixelEvent("Lead");
    }
    await goTo("answer");
  } catch {
    // The shared axios error toast is not wired up here on purpose: the form
    // shows its own message so the page keeps its own visual language.
    failed.value = true;
  } finally {
    isSubmitting.value = false;
  }
};

const onAnswer = async (): Promise<void> => {
  const text = answer.value.trim().slice(0, ANSWER_MAX);

  // Nothing typed: nothing to send. Skipping is the explicit way out.
  if (!text) {
    return;
  }

  isSubmitting.value = true;
  failed.value = false;
  try {
    await submitContestEntry({
      email: email.value.trim(),
      answer: text,
      honeypot: honeypot.value,
    });
    await finish("answered");
  } catch {
    failed.value = true;
  } finally {
    isSubmitting.value = false;
  }
};
</script>
